import React from 'react';
import classnames from 'classnames';
import EditIcon from '@atlaskit/icon/core/edit';
import Button from '@atlaskit/button/new';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { AddContactFields, AddContactSelectFields } from '@css-xp/ppc-add-contact-fields';
import {
  ContactInfo,
  CreditCard,
  InvoiceGroup,
  PaymentMethod,
  PaymentMethodType,
  PaymentMethodTypeV2,
  PaymentOnTerms,
  Ach,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { TermPaymentMethod } from '@css-xp/base/term-payment-method';
import { ContactsTooltip, ContactsTooltipType, Divider, InfoPanel, AchPanel } from '@css-xp/base';
import { NoData } from '@css-xp/ppc-common';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { splitAndCapitalize, isAchPaymentMethod } from '@css-xp/ppc-utils';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { PrimaryContactChangeInfoPanel } from './primary-contact-change-info-panel';
import './billing-profile.scss';
import { token } from '@atlaskit/tokens';

type BillingProfileProps = {
  billingProfileInfo: InvoiceGroup | null | undefined;
  atlassianAccountIds: string[];
  setAtlassianAccountIds: React.Dispatch<React.SetStateAction<string[]>>;
  atlassianAccountEmails: string[];
  setAtlassianAccountEmails: React.Dispatch<React.SetStateAction<string[]>>;
  BillingProfileMoreActions?: React.ReactNode;
  isLoading?: boolean;
  invoiceGroupName?: string;
  Label?: () => void;
  displayPONumber?: boolean;
  shouldShowBillCouplingWarning?: boolean;
  eccLoading?: boolean;
  isAddNewContactDisabled?: boolean;
  isExistingContactRemoveDisabled?: boolean;
  currentUserList?: string[];
  removableCustomerEmailList?: string[];
  showPricingEligibility?: boolean;
  showPrimaryCustomerContacts?: boolean;
  showQuotePrimaryContact?: boolean;
  quotePrimaryContactsAaids?: string[];
  setQuotePrimaryContactsAaids?: React.Dispatch<React.SetStateAction<string[]>>;
  quotePrimaryContactsEmails?: string[];
  setQuotePrimaryContactsEmails?: React.Dispatch<React.SetStateAction<string[]>>;
  quotePrimaryContactName?: string;
  isQuotePrimaryContactsLoading?: boolean;
  secondaryCustomerContacts?: ContactInfo[];
  allowAddingPrimaryContact?: boolean;
  isBulkECCDetailsLoading?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
  onEditPaymentClick?: () => void;
  selectedPaymentMethod?: PaymentMethod | null;
  isPaymentMethodLoading?: boolean;
  isDesignIteration1Enabled?: boolean;
  shouldShowACHPaymentMethod?: boolean;
};

const billingProfileStyleV1 = xcss({
  width: '80%',
});
const billingProfileStyleV2 = xcss({
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid',
  borderColor: 'color.border',
  borderRadius: 'border.radius.200',
  padding: 'space.300',
});
const billingHeaderStyleV1 = xcss({
  padding: 'space.100',
  paddingBottom: 'space.0',
});

const billingHeaderStyleV2 = xcss({
  paddingBlock: 'space.200',
  paddingInline: 'space.0',
});

export const BillingProfile: React.FC<BillingProfileProps> = ({
  billingProfileInfo,
  selectedPaymentMethod,
  isPaymentMethodLoading,
  atlassianAccountIds,
  setAtlassianAccountIds,
  atlassianAccountEmails,
  setAtlassianAccountEmails,
  currentUserList = [],
  BillingProfileMoreActions,
  isLoading,
  invoiceGroupName,
  Label = () => <></>,
  displayPONumber = false,
  shouldShowBillCouplingWarning = false,
  eccLoading = false,
  isAddNewContactDisabled = false,
  isExistingContactRemoveDisabled = false,
  removableCustomerEmailList = [],
  showPricingEligibility = false,
  showPrimaryCustomerContacts = false,
  quotePrimaryContactsAaids,
  setQuotePrimaryContactsAaids,
  quotePrimaryContactsEmails,
  setQuotePrimaryContactsEmails,
  isQuotePrimaryContactsLoading,
  quotePrimaryContactName,
  secondaryCustomerContacts,
  showQuotePrimaryContact = false,
  isBulkECCDetailsLoading = false,
  isMultiCurrencyChangesEnabled = false,
  onEditPaymentClick,
  isDesignIteration1Enabled,
  shouldShowACHPaymentMethod = false,
}) => {
  const soldToAddress = billingProfileInfo?.shipToParty;
  const paymentMethod = selectedPaymentMethod ? selectedPaymentMethod : billingProfileInfo?.defaultPaymentMethod;
  const poNumber = billingProfileInfo?.purchaseOrder?.number;
  const invoiceGroupId = billingProfileInfo?.id;
  const transactionAccountId = billingProfileInfo?.transactionAccountId;

  const [showQuoteContactInfo, setShowQuoteContactInfo] = React.useState(false);

  const getDefaultPaymentSection = (defaultPaymentMethod?: PaymentMethod | null) => {
    if (!defaultPaymentMethod) {
      return <p>No payment method found</p>;
    }

    if (defaultPaymentMethod?.type === PaymentMethodType?.CARD) {
      const defaultPaymentMethodObj = defaultPaymentMethod as CreditCard;

      return (
        <PaymentMethodPanel
          paymentMethod={{
            id: defaultPaymentMethodObj?.id as PaymentMethodId,
            type: defaultPaymentMethodObj?.type,
            // Because the 'card' prop should be used with PaymentMethodType.CARD type
            ...(defaultPaymentMethodObj?.type === PaymentMethodType.CARD && {
              card: {
                brand: defaultPaymentMethodObj?.card?.brand ?? '',
                expMonth: +(defaultPaymentMethodObj?.card?.expirationMonth ?? 0),
                expYear: +(defaultPaymentMethodObj?.card?.expirationYear ?? 0),
                last4: defaultPaymentMethodObj?.card?.last4Numbers ?? '',
                name: defaultPaymentMethodObj?.card?.cardHolderName ?? '',
              },
            }),
            default: defaultPaymentMethodObj?.default ?? false,
          }}
        />
      );
    }

    // In Order summary, there isn't any billing profile with associated payment method as push payments
    /* istanbul ignore next */
    if (defaultPaymentMethod?.type === PaymentMethodType?.DEFERRED) {
      const defaultPaymentMethodObj = defaultPaymentMethod as PaymentOnTerms;
      const hasPayByStatement = defaultPaymentMethodObj?.terms?.payOnStatement;
      const invoiceDueDays = defaultPaymentMethodObj?.terms?.invoiceDueDays;
      const termPaymentMethod = defaultPaymentMethodObj?.paymentMethod;
      const termPaymentType = invoiceDueDays ? `NET${invoiceDueDays}` : termPaymentMethod;
      const totalCreditLimit = defaultPaymentMethodObj?.totalCreditLimit;

      return (
        <TermPaymentMethod
          paymentNetType={hasPayByStatement ? '' : (termPaymentType as string)}
          totalLimit={hasPayByStatement ? undefined : (totalCreditLimit as number)}
          currentTermAccountStatus={billingProfileInfo?.termPaymentStatus?.status}
          hasPayOnAccount={!!hasPayByStatement}
        />
      );
    }

    // @ts-ignore
    if (defaultPaymentMethod?.type === PaymentMethodTypeV2?.PAYPAL) {
      const defaultPaymentMethodObj = defaultPaymentMethod as any;
      return <PaypalPaymentMethod emailID={defaultPaymentMethodObj?.payPal?.email} />;
    }

    if (shouldShowACHPaymentMethod && isAchPaymentMethod(defaultPaymentMethod?.type)) {
      const defaultPaymentMethodObj = defaultPaymentMethod as Ach;
      return (
        <AchPanel
          bankName={defaultPaymentMethodObj?.ach?.bankName}
          last4={defaultPaymentMethodObj?.ach?.last4}
          showInfoInline={false}
        />
      );
    }
  };

  const getPrimaryOrExistingContactEmails = (accountEmails: string[]) => {
    if (showQuoteContactInfo && quotePrimaryContactsAaids?.[0] !== '' && atlassianAccountIds[0] === '') {
      return ['Unassigned'];
    }
    return accountEmails;
  };

  /** show quote primary contact info section only when bp primary contact and quote primary contact are not same as well quote contact is not undefined */
  React.useEffect(() => {
    const showQuotePrimaryContactInfo =
      showQuotePrimaryContact &&
      quotePrimaryContactsEmails?.[0] !== '' &&
      (quotePrimaryContactsEmails?.[0] !== atlassianAccountEmails?.[0] || atlassianAccountEmails?.[0] === '');
    setShowQuoteContactInfo(showQuotePrimaryContactInfo);
  }, [quotePrimaryContactsEmails, atlassianAccountEmails]);

  const secondaryContacts = secondaryCustomerContacts?.filter(
    (contact) => contact?.identifierId === billingProfileInfo?.id
  );

  const BillingProfileName = React.useMemo(() => {
    const prefix = isDesignIteration1Enabled ? '' : 'Billing profile - ';
    return (
      <Heading size="small">
        {invoiceGroupName
          ? `${prefix}${invoiceGroupName}`
          : billingProfileInfo
          ? `${prefix}${billingProfileInfo.name}`
          : `Default billing profile`}
      </Heading>
    );
  }, [invoiceGroupName, billingProfileInfo, isDesignIteration1Enabled]);

  const renderCustomerContactsField = () => {
    if (eccLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader height="20px" />
        </SkeletonLoaderWrapper>
      );
    } else if (showPrimaryCustomerContacts) {
      return (
        <div className="primary-contacts-dropdown">
          <AddContactSelectFields
            isExistingContactRemoveDisabled={true}
            isAddNewContactDisabled={true}
            setAtlassianAccountIds={setAtlassianAccountIds}
            atlassianAccountIds={atlassianAccountIds}
            atlassianAccountEmails={getPrimaryOrExistingContactEmails(atlassianAccountEmails)}
            setAtlassianAccountEmails={setAtlassianAccountEmails}
            currentUserList={atlassianAccountIds}
            userAlreadyExistText="Customer contact already exists"
            emailFieldText="Select or add new contact"
            shouldAllowAddingContact={true}
            textFieldLabel="Enter email"
            nameFieldText="Enter contact full name"
            availableOptions={secondaryContacts?.map((contact: ContactInfo) => ({
              fullName: contact?.name ?? '',
              email: contact?.email ?? '',
              picture: contact?.picture ?? '',
              atlassianAccountId: contact?.accountId ?? '',
            }))}
            isRemoveButtonHidden
          />
        </div>
      );
    }
    return (
      <AddContactFields
        atlassianAccountIds={atlassianAccountIds}
        setAtlassianAccountIds={setAtlassianAccountIds}
        atlassianAccountEmails={atlassianAccountEmails}
        setAtlassianAccountEmails={setAtlassianAccountEmails}
        currentUserList={currentUserList}
        userAlreadyExistText="Customer contact already exists"
        emailFieldText="Enter contact email"
        nameFieldText="Enter contact full name"
        addButtonText="Add customer contact"
        isExistingContactRemoveDisabled={isExistingContactRemoveDisabled}
        isAddNewContactDisabled={showPrimaryCustomerContacts ? true : isAddNewContactDisabled}
        removableCustomerEmailList={removableCustomerEmailList}
      />
    );
  };

  return (
    <Box
      xcss={isDesignIteration1Enabled ? billingProfileStyleV2 : billingProfileStyleV1}
      testId="order-summary-billing-profile"
    >
      {Label()}
      {!isLoading && shouldShowBillCouplingWarning && (
        <div className="billing-coupled-warning-banner" data-testId="billing-coupled-warning-banner">
          <InfoPanel type="warning" message="The billing profiles for some of the subscriptions will be changed." />
        </div>
      )}
      {isLoading || isBulkECCDetailsLoading ? (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="542px" height="156px" marginBottom={'0'} />
        </SkeletonLoaderWrapper>
      ) : (
        <div
          className={classnames({
            'billing-profile-container-content': !isDesignIteration1Enabled,
          })}
        >
          <Flex
            justifyContent="space-between"
            xcss={isDesignIteration1Enabled ? billingHeaderStyleV2 : billingHeaderStyleV1}
          >
            {BillingProfileName}
            {!isDesignIteration1Enabled ? BillingProfileMoreActions : null}
          </Flex>
          <div className="billing-profile-container-content-sold-to-container">
            <label className="billing-profile-container-text-label">Sold-to address</label>
            <div className="billing-profile-container-content-sold-to-container-address">
              {soldToAddress?.addressLine1 ? (
                <>
                  <p>
                    {soldToAddress?.name}, {soldToAddress?.addressLine1},{' '}
                    {soldToAddress?.addressLine2 ? `${soldToAddress?.addressLine2},` : ''}
                  </p>
                  <p>
                    {soldToAddress?.city}, {soldToAddress?.postcode} {soldToAddress?.state}, {soldToAddress?.country}
                  </p>
                </>
              ) : (
                <p>Same as billing address</p>
              )}
              {showPricingEligibility && (
                <p>
                  Price eligibility:{' '}
                  {billingProfileInfo?.shipToPriceEligibility
                    ? splitAndCapitalize(billingProfileInfo?.shipToPriceEligibility)
                    : 'Commercial'}
                </p>
              )}
              {isMultiCurrencyChangesEnabled && (
                <p>Currency: {billingProfileInfo?.currency ? billingProfileInfo?.currency : <NoData />}</p>
              )}
            </div>
          </div>
          <div className="billing-profile-container-content-payment-container">
            <label className="billing-profile-container-text-label">Payment method</label>
            {isPaymentMethodLoading ? (
              <SkeletonLoaderWrapper>
                <SkeletonLoader height="20px" />
              </SkeletonLoaderWrapper>
            ) : (
              <div className="billing-profile-payment-method">
                {getDefaultPaymentSection(paymentMethod)}
                {isMultiCurrencyChangesEnabled && (
                  <AdvocateAccessWall
                    accessType={ADVOCATE_ACCESS_TYPE.UPDATE}
                    resource={ADVOCATE_RESOURCE_TYPE.INVOICE_GROUP}
                  >
                    <Button
                      iconBefore={EditIcon}
                      appearance="subtle"
                      onClick={onEditPaymentClick}
                      color={token('color.text.subtle')}
                      testId="edit-payment-method-btn"
                    >
                      Change
                    </Button>
                  </AdvocateAccessWall>
                )}
              </div>
            )}
          </div>
          <div
            className={classnames('billing-profile-container-content-taxid-container', {
              'billing-profile-container-content-taxid-container-po-number': displayPONumber,
            })}
          >
            {displayPONumber ? (
              <>
                <div>
                  <label className="billing-profile-container-text-label">Tax ID</label>
                  <p>{soldToAddress?.taxId || <NoData />}</p>
                </div>
                <div>
                  <label className="billing-profile-container-text-label">PO number</label>
                  <p>{poNumber || <NoData />}</p>
                </div>
              </>
            ) : (
              <>
                <label className="billing-profile-container-text-label">Tax ID</label>
                <p>{soldToAddress?.taxId || <NoData />}</p>
              </>
            )}
          </div>
          <div className="add-show-customer-contact">
            <Field
              elementAfterLabel={
                <ContactsTooltip
                  type={
                    showPrimaryCustomerContacts
                      ? ContactsTooltipType.PRIMARY_CUSTOMER_CONTACT
                      : ContactsTooltipType.CUSTOMER
                  }
                  iconSize="small"
                />
              }
              label={
                <span className={classnames({ 'label-padding-btm': !showPrimaryCustomerContacts })}>
                  {showPrimaryCustomerContacts ? 'Primary customer contact' : 'Customer contact'}
                </span>
              }
              name="customer-contact"
              isRequired
            >
              {renderCustomerContactsField}
            </Field>
          </div>
          {showQuoteContactInfo && (
            <>
              <Divider />
              {
                <div className="add-show-customer-contact">
                  <Field
                    elementAfterLabel={
                      <ContactsTooltip type={ContactsTooltipType.PRIMARY_QUOTE_CONTACT} iconSize="small" />
                    }
                    label={'Primary customer contact for the quote'}
                    name="customer-contact"
                    isRequired
                  >
                    {() =>
                      isQuotePrimaryContactsLoading ? (
                        <SkeletonLoaderWrapper>
                          <SkeletonLoader height="20px" />
                        </SkeletonLoaderWrapper>
                      ) : (
                        <AddContactFields
                          atlassianAccountIds={quotePrimaryContactsAaids as string[]}
                          setAtlassianAccountIds={setQuotePrimaryContactsAaids as any}
                          atlassianAccountEmails={quotePrimaryContactsEmails as string[]}
                          setAtlassianAccountEmails={setQuotePrimaryContactsEmails as any}
                          userAlreadyExistText="Customer contact already exists"
                          emailFieldText="Enter contact email"
                          nameFieldText="Enter contact full name"
                          addButtonText="Add customer contact"
                          isExistingContactRemoveDisabled={isExistingContactRemoveDisabled}
                          isAddNewContactDisabled
                        />
                      )
                    }
                  </Field>
                </div>
              }
              {
                <PrimaryContactChangeInfoPanel
                  transactionAccountId={transactionAccountId}
                  invoiceGroupId={invoiceGroupId}
                  quoteprimaryContactEmail={quotePrimaryContactsEmails?.[0]}
                  quoteprimaryContactName={quotePrimaryContactName}
                />
              }
            </>
          )}
        </div>
      )}
    </Box>
  );
};
